// =====================================================================
//  LISTADO DE CONCESIONES
//
//  Réplica de vListado() del prototipo: filtros por comuna, estado,
//  interés y tipo, búsqueda libre, orden por cualquier columna,
//  exportación a CSV y respaldo JSON.
// =====================================================================

import { useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useConcesiones, usePuntajes } from '../datos/consultas'
import { Cargando, ErrorCarga, Puntaje, Tag } from '../componentes/ui'
import { Pagina } from '../App'
import { fmt, fmtUtm, num } from '../dominio/formato'
import { ESTADOS, INTERES, type Concesion, type EstadoConcesion, type NivelInteres } from '../tipos/dominio'

type Columna =
  | 'nombre'
  | 'comuna'
  | 'titular'
  | 'adeudado'
  | 'retiro'
  | 'utm'
  | 'estado'
  | 'interes'
  | 'puntaje'

const ORDEN_INTERES: NivelInteres[] = ['DESCARTADA', 'BAJA', 'MEDIA', 'ALTA']

export function Listado() {
  const concesiones = useConcesiones()
  const puntajes = usePuntajes()
  const [, setParams] = useSearchParams()

  const [q, setQ] = useState('')
  const [comuna, setComuna] = useState('')
  const [estado, setEstado] = useState('')
  const [interes, setInteres] = useState('')
  const [tipo, setTipo] = useState('')
  const [orden, setOrden] = useState<Columna>('puntaje')
  const [dir, setDir] = useState<1 | -1>(-1)

  const puntajePor = useMemo(
    () => new Map(puntajes.data?.map((x) => [x.concesion_id, x.puntaje]) ?? []),
    [puntajes.data],
  )

  const filtradas = useMemo(() => {
    const todas = concesiones.data ?? []
    const busca = q.trim().toLowerCase()

    const claves: Record<Columna, (c: Concesion) => string | number> = {
      nombre: (c) => c.nombre,
      comuna: (c) => c.comuna,
      titular: (c) => c.titular,
      adeudado: (c) => num(c.monto_adeudado),
      retiro: (c) => num(c.costo_retiro_dueno),
      utm: (c) => num(c.utm_adeudadas),
      estado: (c) => c.estado,
      interes: (c) => ORDEN_INTERES.indexOf(c.interes),
      puntaje: (c) => puntajePor.get(c.id) ?? 0,
    }
    const clave = claves[orden]

    return todas
      .filter((c) => {
        if (comuna && c.comuna !== comuna) return false
        if (estado && c.estado !== estado) return false
        if (interes && c.interes !== interes) return false
        if (tipo && c.rol_tipo !== tipo) return false
        if (
          busca &&
          !`${c.nombre}${c.titular}${c.ubicacion ?? ''}${c.rol_nacional}`
            .toLowerCase()
            .includes(busca)
        ) {
          return false
        }
        return true
      })
      .sort((a, b) => {
        const x = clave(a)
        const y = clave(b)
        return (x > y ? 1 : x < y ? -1 : 0) * dir
      })
  }, [concesiones.data, q, comuna, estado, interes, tipo, orden, dir, puntajePor])

  if (concesiones.isError) return <ErrorCarga error={concesiones.error} />
  if (concesiones.isLoading || !concesiones.data) return <Cargando />

  const todas = concesiones.data
  const comunas = [...new Set(todas.map((c) => c.comuna))].sort()

  const ordenarPor = (col: Columna) => {
    if (orden === col) setDir((d) => (d === 1 ? -1 : 1))
    else {
      setOrden(col)
      setDir(-1)
    }
  }

  const th = (col: Columna, texto: string, clase?: string) => (
    <th className={clase} onClick={() => ordenarPor(col)}>
      {texto}
      {orden === col ? (dir < 0 ? ' ▾' : ' ▴') : ''}
    </th>
  )

  const abrir = (rolNacional: string) => setParams({ ficha: rolNacional })

  return (
    <>
      <Pagina
        titulo="Listado de concesiones"
        sub="Nóminas de patentes impagas remitidas por la Tesorería Regional de Tarapacá. Haz clic en cualquier fila para abrir la ficha."
      />

      <div className="toolbar">
        <input
          type="search"
          placeholder="Buscar nombre, rol, titular o ubicación…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
        <select value={comuna} onChange={(e) => setComuna(e.target.value)}>
          <option value="">Todas las comunas</option>
          {comunas.map((x) => (
            <option key={x} value={x}>
              {x}
            </option>
          ))}
        </select>
        <select value={estado} onChange={(e) => setEstado(e.target.value)}>
          <option value="">Todo estado</option>
          {(Object.keys(ESTADOS) as EstadoConcesion[]).map((k) => (
            <option key={k} value={k}>
              {ESTADOS[k].t}
            </option>
          ))}
        </select>
        <select value={interes} onChange={(e) => setInteres(e.target.value)}>
          <option value="">Todo interés</option>
          {(Object.keys(INTERES) as NivelInteres[]).map((k) => (
            <option key={k} value={k}>
              {INTERES[k].t}
            </option>
          ))}
        </select>
        <select value={tipo} onChange={(e) => setTipo(e.target.value)}>
          <option value="">Todo tipo</option>
          <option value="C">Constituida (C)</option>
          <option value="T">En trámite (T)</option>
        </select>
        <button className="btn" onClick={() => exportarCSV(filtradas, puntajePor)}>
          Exportar CSV
        </button>
        <button className="btn" onClick={() => respaldoJSON(todas, puntajePor)}>
          Respaldo JSON
        </button>
      </div>

      <div className="card" style={{ padding: '6px 10px 0' }}>
        <table>
          <thead>
            <tr>
              {th('nombre', 'Concesión')}
              {th('comuna', 'Comuna')}
              {th('titular', 'Titular')}
              {th('adeudado', 'Mínimo subasta', 'num')}
              {th('retiro', 'Retiro dueño', 'num')}
              {th('utm', 'UTM', 'num')}
              <th>Tipo</th>
              {th('estado', 'Estado')}
              {th('interes', 'Interés')}
              {th('puntaje', 'Puntaje')}
            </tr>
          </thead>
          <tbody>
            {filtradas.map((c) => (
              <tr
                key={c.id}
                onClick={() => abrir(c.rol_nacional)}
                className={c.estado === 'EN_REMATE' ? undefined : 'inactiva'}
              >
                <td>
                  <div className="nm">{c.nombre}</div>
                  <div className="sub mono">
                    {c.rol_nacional} · {c.ubicacion}
                  </div>
                </td>
                <td className="sub">{c.comuna}</td>
                <td className="sub" style={{ maxWidth: 190 }}>
                  {c.titular}
                </td>
                <td className="num">{fmt(c.monto_adeudado)}</td>
                <td className="num sub">{fmt(c.costo_retiro_dueno)}</td>
                <td className="num sub">{fmtUtm(c.utm_adeudadas)}</td>
                <td>
                  <Tag clase={c.rol_tipo === 'C' ? 't-pagada' : 't-media'}>{c.rol_tipo}</Tag>
                </td>
                <td>
                  <Tag clase={ESTADOS[c.estado].c}>{ESTADOS[c.estado].t}</Tag>
                </td>
                <td>
                  <Tag clase={INTERES[c.interes].c}>{INTERES[c.interes].t}</Tag>
                </td>
                <td>
                  <Puntaje valor={puntajePor.get(c.id) ?? 0} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <div style={{ padding: '11px 4px', fontSize: 12, color: 'var(--txt3)' }}>
          {filtradas.length} de {todas.length} concesiones · mínimo acumulado{' '}
          {fmt(filtradas.reduce((s, c) => s + num(c.monto_adeudado), 0))}
        </div>
      </div>
    </>
  )
}

// ---------------------------------------------------------------------
//  Exportación
// ---------------------------------------------------------------------

function bajar(blob: Blob, nombre: string) {
  const a = document.createElement('a')
  a.href = URL.createObjectURL(blob)
  a.download = nombre
  a.click()
  URL.revokeObjectURL(a.href)
}

function exportarCSV(filas: Concesion[], puntajePor: Map<number, number>) {
  const cabecera = [
    'N', 'Rol nacional', 'Comuna', 'Nombre', 'Ubicacion', 'Titular', 'Girado', 'Adeudado', 'UTM',
    'Prop/Desc', 'Tipo', 'Rol anio', 'Minimo subasta', 'Retiro dueno', 'Estado', 'Interes',
    'Postura', 'Superficie ha', 'Lote', 'Garantia lote', 'Puntaje', 'Verificada', 'Notas',
  ]
  const cuerpo = filas.map((c) => [
    c.numero_orden, c.rol_nacional, c.comuna, c.nombre, c.ubicacion ?? '', c.titular,
    num(c.monto_girado), num(c.monto_adeudado), num(c.utm_adeudadas), c.prop_desc, c.rol_tipo,
    c.rol_anio ?? '', num(c.minimo_subasta), num(c.costo_retiro_dueno),
    ESTADOS[c.estado].t, INTERES[c.interes].t,
    num(c.postura_max) || '', num(c.superficie_ha) || '', c.lote ?? '',
    num(c.monto_adeudado), puntajePor.get(c.id) ?? '',
    c.verificado_en ? 'si' : 'no',
    (c.notas ?? '').replace(/[\r\n;]/g, ' '),
  ])
  // El BOM es lo que hace que Excel en español abra el archivo en UTF-8.
  const csv =
    '﻿' +
    [cabecera, ...cuerpo]
      .map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(';'))
      .join('\n')
  bajar(new Blob([csv], { type: 'text/csv;charset=utf-8' }), 'remate_minero_2026.csv')
}

function respaldoJSON(todas: Concesion[], puntajePor: Map<number, number>) {
  const contenido = {
    exportado: new Date().toISOString(),
    causa: todas[0]?.causa_rol ?? null,
    tribunal: todas[0]?.tribunal ?? null,
    concesiones: todas.length,
    deuda_total: todas.reduce((s, c) => s + num(c.monto_adeudado), 0),
    filas: todas.map((c) => ({ ...c, puntaje: puntajePor.get(c.id) ?? null })),
  }
  bajar(
    new Blob([JSON.stringify(contenido, null, 1)], { type: 'application/json' }),
    'respaldo_remate_minero.json',
  )
}
